import { useCallback, useMemo, useState } from 'react';
import { useToast } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import DecisionFooter from '../../DecisionFooter.jsx';
import { DecisionConfirmModal } from '../shared/index.js';
import { useEventDraft } from '../../../context/useEventDraft.js';
import { useWorkQueueContext } from '../../../context/useWorkQueueContext.js';
import { ArrowUpCircle, XCircle } from 'lucide-react';

/**
 * Train Me decision: Promote to Canon / Reject. The edited value lives in the
 * detail body (`EditBlock`) and is read here via EventDraftProvider.
 */
function TrainMeDecision({ event, actions }) {
  const { t } = useTranslation('work-queues-app');
  const toast = useToast();
  const { completeTrainMe } = useWorkQueueContext();
  const { draft, editing } = useEventDraft();
  const detail = event.detail ?? {};
  const [mode, setMode] = useState(null); // 'promote' | 'reject'
  const value = editing ? draft : (detail.proposedValue ?? '');

  const agentCount = detail.impact?.agents?.length ?? 0;
  const workflowCount = detail.impact?.workflows?.length ?? 0;

  const promoteRows = useMemo(
    () =>
      [
        { label: t('trainMe.field'), value: detail.fieldName },
        { label: t('trainMe.change'), value: `${detail.currentValue} → ${value}` },
        { label: t('trainMe.partition'), value: detail.partition },
        agentCount > 0 ? { label: t('trainMe.agentsLabel'), value: String(agentCount) } : null,
        workflowCount > 0 ? { label: t('trainMe.workflowsLabel'), value: String(workflowCount) } : null,
      ].filter((row) => row?.value),
    [t, detail, value, agentCount, workflowCount],
  );

  const handleCloseMode = useCallback(() => setMode(null), []);

  const handlePromote = useCallback(
    ({ reason }) => {
      completeTrainMe(event.id, { outcome: 'promoted', value, note: reason });
      toast.success({ title: t('trainMe.promotedTitle'), message: t('trainMe.promotedMessage') });
    },
    [completeTrainMe, event.id, t, toast, value],
  );

  const handleReject = useCallback(
    ({ reason }) => {
      completeTrainMe(event.id, { outcome: 'rejected', reason });
      toast.success({ title: t('trainMe.rejectedTitle'), message: t('trainMe.rejectedMessage') });
    },
    [completeTrainMe, event.id, t, toast],
  );

  const primaryActions = useMemo(
    () => [
      { id: 'promote', label: t('trainMe.promote'), icon: ArrowUpCircle, onClick: () => setMode('promote') },
      { id: 'reject', label: t('trainMe.reject'), icon: XCircle, variant: 'danger', onClick: () => setMode('reject') },
    ],
    [t],
  );

  return (
    <>
      <DecisionFooter
        event={event}
        primaryActions={primaryActions}
        onSkip={actions.skip}
        onAsk={actions.ask}
        onEscalate={actions.escalate}
        onTrace={actions.trace}
        extraMenuItems={actions.extraMenuItems}
        sticky={actions.sticky}
      />

      <DecisionConfirmModal
        isOpen={mode === 'promote'}
        onClose={handleCloseMode}
        icon={<ArrowUpCircle size={20} className="text-emerald-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('trainMe.promoteTitle')}
        subtitle={t('trainMe.promoteSubtitle')}
        summaryRows={promoteRows}
        showReason
        reasonLabel={t('trainMe.noteLabel')}
        reasonPlaceholder={t('trainMe.notePlaceholder')}
        confirmLabel={t('trainMe.promote')}
        onConfirm={handlePromote}
      />

      <DecisionConfirmModal
        isOpen={mode === 'reject'}
        onClose={handleCloseMode}
        icon={<XCircle size={20} className="text-rose-500" />}
        breadcrumb={`${event.id} · ${event.refLabel ?? ''}`}
        title={t('trainMe.rejectTitle')}
        subtitle={t('trainMe.rejectSubtitle')}
        requireReason
        reasonLabel={t('trainMe.rejectReasonLabel')}
        reasonPlaceholder={t('trainMe.rejectReasonPlaceholder')}
        confirmLabel={t('trainMe.reject')}
        confirmVariant="danger"
        onConfirm={handleReject}
      />
    </>
  );
}

export default TrainMeDecision;
